import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Container, Row, Col } from "react-bootstrap";
import Select from "react-dropdown-select";
import moment from "moment";
import axios from "axios";
import { config } from "../../App";

const Loader = () => (
    <div className="loader-container">
        <img src="./images/loader.gif" alt="loading" />
    </div>
);

const imageUrlToBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png").split(",")[1]);
        };
        img.onerror = () => reject(new Error("Image load failed: " + url));
        img.src = url + "?t=" + Date.now(); // cache bust
    });

const embedWatermarkInPdf = async (pdfBytes, watermarkBase64) => {
    if (!window.PDFLib) {
        await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js";
            script.onload = resolve;
            script.onerror = () => reject(new Error("PDFLib load failed"));
            document.head.appendChild(script);
        });
    }

    const { PDFDocument } = window.PDFLib;

    const pdfDoc = await PDFDocument.load(pdfBytes.slice(0));

    const logoBytes = Uint8Array.from(atob(watermarkBase64), (c) => c.charCodeAt(0));
    const logoImage = await pdfDoc.embedPng(logoBytes);

    for (const page of pdfDoc.getPages()) {
        const { width, height } = page.getSize();

        const desiredWidth = width * 0.4;
        const scale = desiredWidth / logoImage.width;
        const imgWidth = logoImage.width * scale;
        const imgHeight = logoImage.height * scale;

        page.drawImage(logoImage, {
            x: (width - imgWidth) / 2,
            y: (height - imgHeight) / 2,
            width: imgWidth,
            height: imgHeight,
            opacity: 0.15,
        });
    }

    return pdfDoc.save();
};

// ─── PDF.js ko load karo ─────────────────────────────────────────────────────
const loadPdfJs = () =>
    new Promise((resolve, reject) => {
        if (window.pdfjsLib) return resolve(window.pdfjsLib);

        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
            resolve(window.pdfjsLib);
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });

// ─── Watermark image ko preload karo ─────────────────────────────────────────
const loadWatermarkImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

// ─── PDF ko Canvas mein render karo watermark ke saath ───────────────────────
const renderPdfWithWatermark = async (pdfBytes, containerEl, watermarkImg) => {
    const pdfjsLib = await loadPdfJs();

    const pdfData = new Uint8Array(pdfBytes);
    const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;

    containerEl.innerHTML = "";

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.5 });

        // ── Har page ke liye wrapper div ──────────────────────────────────────
        const pageWrapper = document.createElement("div");
        pageWrapper.style.cssText = `
            position: relative;
            margin: 0 auto 16px auto;
            width: ${viewport.width}px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            background: #fff;
        `;

        // ── PDF page canvas ───────────────────────────────────────────────────
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.display = "block";

        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;

        if (watermarkImg) {
            const wDesiredWidth = viewport.width * 0.65;
            const wScale = wDesiredWidth / watermarkImg.naturalWidth;
            const wW = watermarkImg.naturalWidth * wScale;
            const wH = watermarkImg.naturalHeight * wScale;
            const wX = (viewport.width - wW) / 2;
            const wY = (viewport.height - wH) / 2;

            ctx.globalAlpha = 0.15;
            ctx.drawImage(watermarkImg, wX, wY, wW, wH);
            ctx.globalAlpha = 1.0;
        }

        pageWrapper.appendChild(canvas);
        containerEl.appendChild(pageWrapper);
    }
};

const AppraisalLatter = () => {
    const token = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("userId");
    const doj = sessionStorage.getItem("doj");

    const [selectedYear, setSelectedYear] = useState();
    const [loading, setLoading] = useState(false);
    const [yearError, setYearError] = useState("");
    const [years, setYears] = useState([]);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [isSearched, setIsSearched] = useState(false);
    const [fileName, setFileName] = useState("");
    const [rawPdfBytes, setRawPdfBytes] = useState(null);

    const pdfContainerRef = useRef(null);
    const watermarkImgRef = useRef(null);

    useEffect(() => {
        loadWatermarkImage("./images/logo-3.png")
            .then((img) => { watermarkImgRef.current = img; })
            .catch(() => { watermarkImgRef.current = null; });
    }, []);

    const getFinalYearsData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetAppraisalFinYear?doj=${moment(doj, "DD/MMM/YYYY").format("DD/MM/YYYY")}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;
            if (!data?.status) return;

            const formattedYears = data.Data.map((item) => ({
                label: item.FinYear,
                value: item.StartYear,
                id: item.FinYearID,
            }));

            setYears(formattedYears);

            const defaultYearObj = formattedYears.find(
                (y) => y.label === data.DefaultFinYear
            );

            if (defaultYearObj) {
                setSelectedYear([defaultYearObj]);
            }
        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getFinalYearsData();
    }, []);

    useEffect(() => {
        return () => {
            if (pdfUrl && pdfUrl !== "loaded") URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl]);

    const validateForm = () => {
        setYearError("");
        if (!selectedYear || !selectedYear.length) {
            setYearError("Please select a Year");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSearched(true);
        setRawPdfBytes(null);

        if (pdfUrl && pdfUrl !== "loaded") URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);

        if (pdfContainerRef.current) {
            pdfContainerRef.current.innerHTML = "";
        }

        if (!validateForm()) return;

        setLoading(true);

        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetAppraisalLetter?employeeId=${userId}&finYear=${selectedYear[0].value}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const data = response.data;
            if (!data?.status) throw new Error(data?.ResponseMessage || "API failed");

            const base64String = data.FileBase64;
            if (!base64String) throw new Error("No Base64 file received");

            const byteCharacters = atob(base64String);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }

            setFileName(data.FileName);
            const downloadBuffer = byteArray.buffer.slice(0);
            const renderBuffer = byteArray.buffer.slice(0);
            setRawPdfBytes(downloadBuffer);
            setPdfUrl("loaded");

            if (pdfContainerRef.current) {
                await renderPdfWithWatermark(
                    renderBuffer,
                    pdfContainerRef.current,
                    watermarkImgRef.current
                );
            }
        } catch (err) {
            console.error("PDF fetch error:", err);
            setPdfUrl(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!rawPdfBytes) return;

        setLoading(true);
        try {
            const watermarkBase64 = await imageUrlToBase64("./images/logo-3.png");
            const watermarkedBytes = await embedWatermarkInPdf(rawPdfBytes, watermarkBase64);

            const blob = new Blob([watermarkedBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || "Appraisal_Letter.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Watermark embed error:", err);
            alert("Failed to embed watermark. Downloading without watermark.");

            const blob = new Blob([rawPdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || "Appraisal_Letter.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setLoading(false);
        }
    };

    const hasPdf = pdfUrl === "loaded" && rawPdfBytes;

    return (
        <>
            <section className="px-4">
                {loading && <Loader />}
                {loading && <div className="backdrop" />}
                <Container fluid>
                    <div className="card border-0 p-4 pb-2">
                        <h1 className="fs-22 fw-semibold mb-4">Appraisal Letter</h1>
                        <Row className="mb-3">
                            <Col lg={4} className="mb-4">
                                <Form.Group controlId="year">
                                    <Form.Label className="mb-2">
                                        Financial Year <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={years}
                                        values={selectedYear}
                                        onChange={(val) => setSelectedYear(val)}
                                        placeholder="Select Financial Year"
                                        className="form-control shadow-none"
                                    />
                                    {yearError && (
                                        <div className="text-danger mt-1 small font-sm">{yearError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 d-flex align-items-end">
                                <div className="d-flex gap-3">
                                    <Button type="submit" onClick={handleSubmit} className="btn btn-primary font-lg">
                                        Submit
                                    </Button>
                                    {hasPdf && (
                                        <Button onClick={handleDownload} type="button" className="btn btn-secondary font-lg">
                                            Download
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="pdfViewSec mt-5 mb-5 card border-0 p-4">
                        <Row>
                            <Col lg={12}>
                                <div
                                    ref={pdfContainerRef}
                                    style={{
                                        maxHeight: "800px",
                                        overflowY: "auto",
                                        border: hasPdf ? "1px solid #ced4da" : "none",
                                        borderRadius: "6px",
                                        background: hasPdf ? "#f8f9fa" : "transparent",
                                        padding: hasPdf ? "16px" : "0",
                                    }}
                                />

                                {isSearched && !hasPdf && !loading && (
                                    <div className="card border-0 p-4 text-center text-muted font-md">
                                        <img
                                            src="images/data-not-found.svg"
                                            className="img-fluid m-auto mt-5 mb-5 pt-5"
                                            alt="No data"
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Container>
            </section>
        </>
    );
};

export default AppraisalLatter;